import { claimDetailFooterSlot, provideDetailFooterSlot } from '@modules/common/components/detail-breadcrumbs/detail-footer-slot';
import { APP_CRUMBS, TREE_CRUMBS, UNTITLED_CRUMB, DetailNavigation } from '@modules/common/components/detail-breadcrumbs/detail-navigation';
import { type DetailCrumb } from '@modules/common/components/detail-breadcrumbs/detail-breadcrumbs.component';
import { NotesAppStateService } from '@modules/notes/services/state.service';
import { MainViewService } from '@modules/common/services/main-view.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { Application } from '@modules/common/types/application';
import { Component, computed, inject, Injector, Input, OnInit, signal, type SimpleChanges } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAILBOX_TENANT_ID, OWNING_SPACE_ID } from '@modules/common/interceptors/spaces.interceptor';
import { type StitchRequestConfig } from '@modules/common/services/base-stitch.service';
import { NotesTenantScopeService } from '@modules/notes/services/notes-tenant-scope.service';
import {
  type WorkspaceIdentity,
  WorkspaceIdentityService,
} from '@modules/common/services/workspace-identity.service';
import { AlertService } from '@modules/alert/services/alert.service';
import { FullFormBaseComponent } from '@modules/common/components/full-form-base/full-form-base.component';
import { BaseAppStateService } from '@modules/common/services/base-app-state.service';
import { type ContainerDndConfig, ContainerDndService } from '@modules/common/services/container-dnd.service';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { Tab } from '@modules/common/types/tab';
import { type DragData, DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import type { AutocompleteFactory } from '@frontendlib/common/types/autocomplete-factory';
import { NotebooksService } from '@modules/notes/services/notebooks.service';
import { NotesListStateService } from '@modules/notes/services/notes-list-state.service';
import { NotesService } from '@modules/notes/services/notes.service';
import { Notebook, type NotebookFormGroupConfig } from '@modules/notes/types/notebook';
import { NotesFilters } from '@modules/notes/types/notes-filters';
import { NotesListState } from '@modules/notes/types/notes-list-state';
import { StateKey } from '@modules/settings/types/state-key';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { combineLatest } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';

declare module '@modules/settings/types/state' {
  export interface State {
    [StateKey.notesFFListState]?: NotesListState;
  }
}

@Component({
  selector: 'app-notebook-form',
  templateUrl: './notebook-form.component.html',
  styleUrls: ['./notebook-form.component.less'],
  standalone: false,
  providers: [
    ...NotesListStateService.providers({
      stateKey: StateKey.notesFFListState,
    }),
    WorkspaceIdentityService,
    provideDetailFooterSlot(),
  ],
})
export class NotebookFormComponent
  extends FullFormBaseComponent<StitchType.NOTEBOOK, Notebook, NotebookFormGroupConfig>
  implements OnInit
{
  public changesKey = 'notebook';
  tabs: Tab[] = [
    { title: 'Notes', value: 'notebook' },
    { title: 'Details', value: 'details' },
    { title: 'Attachments', value: 'attachments' },
    { title: 'Stitch', value: 'stitch' },
    { title: 'Comments', value: 'comments' },
  ];
  protected override attachmentsMenuStateVariant = 'notebooks';
  public parentSuggestions: AutocompleteFactory<Notebook>;
  public notesFilters: NotesFilters;

  protected dragDataType = DragDataTypes.notebook;
  protected override childItemDragType = DragDataTypes.note;
  protected override childItemContainerIdField = 'notebookId';

  @Input() notebook = new Notebook();

  /** stitch-app-shell breadcrumbs, as on every detail page. */
  protected readonly navigation = new DetailNavigation(NotesAppStateService, { appLabel: APP_CRUMBS.notes });
  /** The item list's pager joins the breadcrumbs row in the main view. */
  private readonly footerSlot = claimDetailFooterSlot(this.navigation.enabled);

  /** [workspace ›] Notes app › Notebooks › this notebook. */
  protected breadcrumbs(): DetailCrumb[] {
    return this.navigation.crumbs({
      view: TREE_CRUMBS.notebooks,
      fallbackView: TREE_CRUMBS.notebooks,
      itemTitle: this.stitchItem?.title || UNTITLED_CRUMB,
    });
  }

  private readonly notesListStateService = inject(NotesListStateService, { self: true });
  private readonly containerDndService = inject(ContainerDndService);
  private readonly alertService = inject(AlertService);
  private readonly appStateService = inject(BaseAppStateService);
  private readonly archiveMainView = inject(MainViewService);
  private readonly archiveSearch = inject(AdvancedSearchService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;

  private dndConfig(): ContainerDndConfig {
    return {
      containerType: DragDataTypes.notebook,
      childItemType: DragDataTypes.note,
      childService: this.notesService,
      containerIdField: 'notebookId',
      appStateService: this.appStateService,
      spaceId: this.stitchItem?.accountId ?? this.spaceContextStore.contextId(),
      // Round 17's live cross-workspace note copy: a note is the one child type this app duplicates
      // across tenants today. Declared here, not only on the rail's own early return
      // (`notes/side-bar.component.ts:223`), so the seam's gate (`ContainerDndService`) keeps Copy live
      // for this config too, rather than depending solely on never being reached (#1145 review F2).
      // Notebooks are NOT this config's childItemType and stay refused everywhere.
      crossTenantCopySupported: true,
    };
  }

  private readonly notesTenantScopeService = inject(NotesTenantScopeService, { optional: true });
  private readonly workspaceIdentityService = inject(WorkspaceIdentityService);

  /** The team the caller is inside, or `undefined` outside one — gates the Sharing Header. */
  protected readonly workspaceIdentity = signal<WorkspaceIdentity | undefined>(undefined);

  /** The Sharing Header's current choice; consulted for a CREATE only. */
  protected readonly sharingTenantId = signal<string | null>(null);

  /** Names the destination on the primary button (#1016 r2 row 2c). */
  protected readonly saveLabel = computed(() => {
    const identity = this.workspaceIdentity();
    if (!identity) {
      return 'Save';
    }
    return this.sharingTenantId() === identity.spaceTenantId ? 'Save to team workspace' : 'Save to my workspace';
  });

  constructor(
    private notebooksService: NotebooksService,
    private notesService: NotesService,
    injector: Injector,
  ) {
    super(injector, notebooksService);

    // The one seed rule for every adopting form (#1128 review F3): seed from the rail, then RE-SEED on
    // identity delivery with `?? identity.memberTenantId`, so an unresolved rail cannot leave the bound
    // tenant `null` while the control reads "Private to me" — a create would then omit `space_id` and
    // land wherever the surrounding context resolved to. Tasks' shape
    // (`task-form.component.ts`, `project-form.component.ts`), adopted here so the four hosts agree.
    this.sharingTenantId.set(this.notesTenantScopeService?.effectiveTenantId() ?? null);
    this.workspaceIdentityService
      .current()
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((identity) => {
        this.workspaceIdentity.set(identity);
        if (!this.stitchItem?.id && identity) {
          this.sharingTenantId.set(
            this.stitchItem?.accountId ?? this.suppliedTenantId() ?? this.notesTenantScopeService?.effectiveTenantId() ?? identity.memberTenantId,
          );
        }
      });
  }

  /**
   * Same rule as the note form: an existing notebook is written back to the tenant it lives under, a
   * new one goes where the Sharing Header says (#1016 r2 rows 2d and 3).
   */
  protected override requestConfig(): StitchRequestConfig {
    if (this.stitchItem?.accountId) {
      return { tenantItems: [this.stitchItem] };
    }

    const tenantId = this.sharingTenantId();
    if (!tenantId) {
      return super.requestConfig();
    }
    return { requestContext: new HttpContext().set(OWNING_SPACE_ID, tenantId) };
  }

  private suppliedTenantId(): string | undefined {
    return this.requestContext?.get(OWNING_SPACE_ID) ?? this.requestContext?.get(MAILBOX_TENANT_ID) ?? undefined;
  }

  public override ngOnChanges(changes: SimpleChanges): void {
    if ((changes.notebook || changes.requestContext) && !this.stitchItem?.id) {
      this.sharingTenantId.set(
        this.stitchItem?.accountId ??
          this.suppliedTenantId() ??
          this.notesTenantScopeService?.effectiveTenantId() ??
          this.workspaceIdentity()?.memberTenantId ?? null,
      );
    }
    super.ngOnChanges(changes);
  }

  /**
   * Lifecycle
   */

  ngOnInit() {
    super.ngOnInit();

    this.stitchEventsService
      .getEvents(() => [this.stitchItem.accountId], StitchType.NOTEBOOK, SignalStitchEventType.ARCHIVED)
      .pipe(
        filter((event) => event.spaceId === this.stitchItem.accountId && event.ids?.includes(this.stitchItem.id)),
        filter(() => {
          const opened = this.archiveMainView.getCurrentMainView();
          return (
            opened?.itemType === StitchType.NOTEBOOK &&
            opened.id === this.stitchItem.id &&
            opened.accountId === this.stitchItem.accountId
          );
        }),
        takeUntil(this.alive),
      )
      .subscribe(() => {
        this.archiveMainView.clearMainView();
        const state = this.archiveSearch.getStateSync();
        state[Application.notes].containersIds = [];
        state[Application.notes].containerName = null;
        this.archiveSearch.setState(state);
        this.appStateService.setVirtualFolder('archived');
      });

    // A saved notebook's audience mirrors its item-carried tenant; service methods derive from the item itself.
    this.stitchItemChanged.pipe(takeUntil(this.alive)).subscribe(() => {
      if (this.stitchItem?.id && this.stitchItem.accountId) {
        this.sharingTenantId.set(this.stitchItem.accountId);
      }
    });

    this.stitchItemChanged.pipe(takeUntil(this.alive)).subscribe(() => {
      this.parentSuggestions = this.notebooksService.getAutocompleteSuggestions({ exceptIds: [this.stitchItem.id] });
    });

    this.stitchItemChanged
      .pipe(
        switchMap(() => combineLatest([this.notesListStateService.value().pipe(take(1)), this.displayTimeZone$])),
        takeUntil(this.alive),
      )
      .subscribe(([, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState reads it to reckon its day windows in the account
        // zone (SBUX-829).
        this.notesFilters = new NotesFilters({ containersIds: [this.stitchItem.id], displayTimeZone });
        this.notesFilters.applyListState(this.notesListStateService.state);
      });
  }

  /**
   * Actions
   */

  dndDrop(dragData: DragData): void {
    this.containerDndService.handleDndDrop(
      this.stitchItem,
      dragData,
      this.dndConfig(),
      this.stitchService,
      this.alertService,
    );
  }

  /**
   * Helpers
   */

  get stitchItem(): Notebook {
    return this.notebook;
  }

  protected shouldRefreshList(prev, current) {
    return Notebook.shouldRefreshList(prev, current);
  }

  protected fromFormGroup(): Notebook {
    return Notebook.fromFormGroup(this.form);
  }

  protected asFormGroup() {
    return this.notebook.asFormGroup({
      notebooksService: this.notebooksService,
      maxAttachmentSize: this.maxUploadSize(),
    });
  }
}
